import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { RetryableError } from '../../../shared/RetryableError';
import { GroupedCombobox, type ComboLoadResult, type ComboOption } from '../../job-roles/components/guided/GroupedCombobox';
import type { ControlA11y } from '../../job-roles/guidance/GuidedField';
import { healthcareErrorMessage } from '../healthcareErrors';
import { healthcareRatesGateway } from '../index';
import {
  HEALTHCARE_SECTOR_CODE,
  INDUSTRY_DESCRIPTION_MAX,
  INDUSTRY_HELP,
  MAX_ADDITIONAL_INDUSTRIES,
  type CompanyIndustryProfile,
  type IndustryKind,
  type IndustrySelection,
  type IndustrySelectionInput,
  type NaicsSector,
  type PreviewRow,
} from '../types';
import { IndustryChangePreviewPanel } from './IndustryChangePreviewPanel';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

export interface IndustryProfileSectionProps {
  profile: CompanyIndustryProfile;
  canManage: boolean;
  onSaved: (profile: CompanyIndustryProfile) => void;
  /** The version on screen is out of date (409): the page re-reads the profile. */
  onReload: () => void;
  /** The lists the page already has, so a changed answer can show what it would change before it is saved. Without them no preview is shown. */
  catalogs?: { serviceTypes: readonly PreviewRow[]; disciplines: readonly PreviewRow[] | null } | undefined;
}

/** What the person has chosen, before it is saved. `choice` is the sector code, or one of the two special answers. */
interface Draft {
  choice: string;
  detailCode: string;
  detailTitle: string;
  description: string;
  template: string;
}

const NOT_SURE = '__not-sure__';
const CUSTOM = '__custom__';
const DETAIL_LIMIT = 25;

const SPECIAL_OPTIONS: ComboOption[] = [
  { value: NOT_SURE, label: 'I’m not sure', detail: 'EPN will not suggest anything. You can add your own worker categories, work types and rates.' },
  { value: CUSTOM, label: 'Other or custom industry', detail: 'Describe your industry in your own words.' },
];

function draftFrom(selection: IndustrySelection | null): Draft {
  if (!selection) return { choice: '', detailCode: '', detailTitle: '', description: '', template: '' };
  const choice = selection.kind === 'NotSure' ? NOT_SURE : selection.kind === 'Custom' && !selection.sectorCode ? CUSTOM : (selection.sectorCode ?? CUSTOM);
  return {
    choice, detailCode: selection.detailCode ?? '', detailTitle: selection.detailTitle ?? '', description: selection.customDescription ?? '', template: selection.healthcareTemplate ?? '',
  };
}

function toInput(draft: Draft): IndustrySelectionInput {
  if (draft.choice === NOT_SURE) return { kind: 'NotSure' };
  const sector = draft.choice === CUSTOM ? undefined : draft.choice;
  return {
    kind: draft.choice === CUSTOM ? 'Custom' : 'Chosen',
    sectorCode: sector,
    detailCode: sector && draft.detailCode ? draft.detailCode : undefined,
    customDescription: draft.description.trim() || undefined,
    healthcareTemplate: sector === HEALTHCARE_SECTOR_CODE && draft.template ? draft.template : undefined,
  };
}

/** The complaint for a draft that cannot be saved, or null. Kept in step with the server's rules so the person is told before the request. */
function problemWith(draft: Draft, label: string): string | null {
  if (!draft.choice) return `${label}: choose an industry group, “I’m not sure” or “Other or custom industry”.`;
  if (draft.choice === CUSTOM && !draft.description.trim()) return `${label}: describe your industry in your own words.`;
  if (draft.description.trim().length > INDUSTRY_DESCRIPTION_MAX) return `${label}: the description must be ${INDUSTRY_DESCRIPTION_MAX} characters or fewer.`;
  return null;
}

const a11y = (id: string, field: string, describedBy?: string): ControlA11y => ({ id, 'data-field': field, 'aria-describedby': describedBy, 'aria-invalid': undefined, 'aria-required': undefined });

function summary(selection: IndustrySelection | null): string {
  if (!selection) return 'Not set yet';
  if (selection.kind === 'NotSure') return 'Not sure';
  const parts = [selection.healthcareTemplateLabel ?? null, selection.detailTitle ?? selection.sectorTitle, selection.customDescription].filter((p): p is string => Boolean(p));
  return [...new Set(parts)].join(' · ') || 'Not set yet';
}

/**
 * Company industry: the broad industry group (one of the 20 NAICS sectors), an optional detailed industry found by name, keyword or code, an optional
 * description in the company's own words, “I’m not sure” and “Other or custom industry”. It only suggests starting worker categories, work types and pay
 * bases; it never sets pay or changes payroll taxes. Additional industries stay collapsed until asked for, so the first screen stays simple.
 */
export function IndustryProfileSection({ profile, canManage, onSaved, onReload, catalogs }: IndustryProfileSectionProps) {
  const uid = useId();
  const [sectors, setSectors] = useState<NaicsSector[] | null>(null);
  const [sectorsError, setSectorsError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(profile.primary));
  const [additional, setAdditional] = useState<Draft[]>(() => profile.additional.map((a) => draftFrom(a)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    healthcareRatesGateway
      .listNaicsSectors(controller.signal)
      .then((list) => { if (!controller.signal.aborted) { setSectors(list); setSectorsError(null); } })
      .catch((err: unknown) => { if (!controller.signal.aborted) setSectorsError(healthcareErrorMessage(err, 'The industry list could not be loaded.')); });
    return () => controller.abort();
  }, [attempt]);

  const sectorTitle = useCallback((code: string) => sectors?.find((s) => s.code === code)?.title ?? code, [sectors]);

  const loadSectors = useMemo(
    () => async (query: string): Promise<ComboLoadResult> => {
      const q = query.trim().toLowerCase();
      const matching = (sectors ?? []).filter((s) => !q || s.title.toLowerCase().includes(q) || s.code.startsWith(q));
      const special = SPECIAL_OPTIONS.filter((o) => !q || o.label.toLowerCase().includes(q));
      return {
        groups: [
          { key: 'sectors', label: 'Industry groups', options: matching.map((s) => ({ value: s.code, label: s.title, meta: s.code })) },
          { key: 'other', label: 'Not listed or not sure', options: special },
        ],
        notice: matching.length === 0 && special.length === 0 ? `No industry group matches “${query}”.` : null,
      };
    },
    [sectors],
  );

  const draftLabel = (d: Draft) => (d.choice === NOT_SURE ? 'I’m not sure' : d.choice === CUSTOM ? 'Other or custom industry' : d.choice ? sectorTitle(d.choice) : '');
  const sector = draft.choice && draft.choice !== NOT_SURE && draft.choice !== CUSTOM ? draft.choice : '';

  const loadDetails = useMemo(
    () => async (query: string, signal: AbortSignal): Promise<ComboLoadResult> => {
      const result = await healthcareRatesGateway.searchNaicsIndustries({ sector: sector || undefined, q: query.trim() || undefined, limit: DETAIL_LIMIT }, signal);
      return {
        groups: [{ key: 'industries', label: 'Detailed industries', options: result.items.map((i) => ({ value: i.code, label: i.title, meta: i.code })) }],
        notice: result.items.length === 0
          ? (query ? `No detailed industry matches “${query}”. You can describe your industry in your own words below.` : 'No detailed industries to show.')
          : result.hasMore ? `Showing the first ${DETAIL_LIMIT}. Type a word or a code to narrow the list.` : null,
      };
    },
    [sector],
  );

  const saved = profile.primary;
  const changed = JSON.stringify([toInput(draft), additional.map(toInput)]) !== JSON.stringify([saved ? toInput(draftFrom(saved)) : { kind: 'Chosen' }, profile.additional.map((a) => toInput(draftFrom(a)))]);
  const problem = problemWith(draft, 'Your industry') ?? additional.map((a, i) => problemWith(a, `Additional industry ${i + 1}`)).find((p) => p !== null) ?? null;

  async function save() {
    setBusy(true);
    setError(null);
    setStatus(null);
    setConflict(false);
    try {
      const result = await healthcareRatesGateway.setIndustryProfile({
        primary: toInput(draft),
        additional: additional.map(toInput),
        expectedConcurrencyVersion: profile.source === 'Profile' ? profile.concurrencyVersion : undefined,
      });
      onSaved(result);
      setDraft(draftFrom(result.primary));
      setAdditional(result.additional.map((a) => draftFrom(a)));
      setStatus('Your industry was saved. It only changes which worker categories and work types are suggested first. Nothing about pay changed.');
    } catch (err) {
      const message = healthcareErrorMessage(err, 'The industry could not be saved.');
      setConflict(typeof err === 'object' && err !== null && (err as { status?: number }).status === 409);
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  const updateAdditional = (index: number, patch: Partial<Draft>) => setAdditional((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  return (
    <section className="hdc-section" aria-labelledby={`${uid}-title`}>
      <h3 id={`${uid}-title`}>Company industry</h3>
      <p className="jr-hint" id={`${uid}-hint`}>{INDUSTRY_HELP}</p>

      {profile.needsReview && (
        <p className="jr-readonly" role="note" data-testid="industry-needs-review">
          Your company was set up before industries were added, with “General (non-healthcare)”. EPN did not guess an industry for you. Choose the one that fits when you are ready; until then
          everything keeps working as it does today.
        </p>
      )}

      {sectorsError && <RetryableError message={sectorsError} onRetry={() => { setSectors(null); setAttempt((n) => n + 1); }} />}

      {!canManage ? (
        <>
          <p data-testid="industry-readonly">
            <strong>Industry:</strong> {summary(profile.primary)}
          </p>
          {profile.additional.length > 0 && <p><strong>Also:</strong> {profile.additional.map(summary).join('; ')}</p>}
          <p className="jr-readonly" role="note">You can see the industry but not change it.</p>
        </>
      ) : (
        <>
          <div className="jr-grid">
            <div className="jr-field">
              <label htmlFor={`${uid}-group`}>Industry group <span aria-hidden="true" className="jr-req">*</span></label>
              <GroupedCombobox
                a11y={{ ...a11y(`${uid}-group`, 'industry-group', `${uid}-hint`), 'aria-required': true }}
                value={draft.choice}
                valueLabel={draftLabel(draft)}
                load={loadSectors}
                disabled={busy || sectors === null}
                placeholder={sectors === null && !sectorsError ? 'Loading industry groups…' : 'Search industry groups'}
                clearLabel="industry group"
                emptyText="No industry group matches. Try a different word."
                onSelect={(o) => { setDraft((d) => ({ ...d, choice: o.value, detailCode: '', detailTitle: '', template: o.value === HEALTHCARE_SECTOR_CODE ? d.template : '' })); setStatus(null); }}
              />
            </div>

            {sector && (
              <div className="jr-field">
                <label htmlFor={`${uid}-detail`}>Detailed industry (optional)</label>
                <GroupedCombobox
                  key={sector}
                  a11y={a11y(`${uid}-detail`, 'industry-detail')}
                  value={draft.detailCode}
                  valueLabel={draft.detailTitle ? `${draft.detailTitle} (${draft.detailCode})` : draft.detailCode}
                  load={loadDetails}
                  disabled={busy}
                  placeholder="Search by name, keyword or code"
                  clearLabel="detailed industry"
                  emptyText="No detailed industry matches. Try a different word."
                  onSelect={(o) => { setDraft((d) => ({ ...d, detailCode: o.value, detailTitle: o.label })); setStatus(null); }}
                  onClear={() => setDraft((d) => ({ ...d, detailCode: '', detailTitle: '' }))}
                />
                <p className="jr-hint">You do not need to know industry codes. Search by what the company does.</p>
              </div>
            )}

            {sector === HEALTHCARE_SECTOR_CODE && (
              <div className="jr-field">
                <label htmlFor={`${uid}-template`}>Healthcare type (optional)</label>
                <select id={`${uid}-template`} value={draft.template} disabled={busy} onChange={(e) => { setDraft((d) => ({ ...d, template: e.target.value })); setStatus(null); }}>
                  <option value="">Not specified: suggest every healthcare default</option>
                  {profile.healthcareTemplates.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </div>
            )}

            {draft.choice !== '' && draft.choice !== NOT_SURE && (
              <div className="jr-field jr-grid__wide">
                <label htmlFor={`${uid}-description`}>{draft.choice === CUSTOM ? 'Describe your industry' : 'Describe your industry in your own words (optional)'}{draft.choice === CUSTOM && <span aria-hidden="true" className="jr-req"> *</span>}</label>
                <input
                  id={`${uid}-description`} value={draft.description} maxLength={INDUSTRY_DESCRIPTION_MAX} autoComplete="off" disabled={busy}
                  aria-required={draft.choice === CUSTOM ? true : undefined} aria-describedby={`${uid}-description-hint`}
                  onChange={(e) => { setDraft((d) => ({ ...d, description: e.target.value })); setStatus(null); }}
                />
                <p id={`${uid}-description-hint`} className="jr-hint">
                  {draft.choice === CUSTOM ? 'Use this when none of the industry groups describes your company.' : 'Use this when the detailed industry does not describe your company accurately.'}{' '}
                  {draft.description.length} of {INDUSTRY_DESCRIPTION_MAX} characters.
                </p>
              </div>
            )}
          </div>

          {additional.length > 0 && (
            <fieldset className="hdc-fieldset hdc-additional" data-testid="additional-industries">
              <legend>Additional industries</legend>
              {additional.map((row, i) => (
                <div className="hdc-additional__row" key={i}>
                  <div className="jr-field">
                    <label htmlFor={`${uid}-extra-${i}`}>Industry group {i + 2}</label>
                    <select id={`${uid}-extra-${i}`} value={row.choice} disabled={busy} onChange={(e) => { updateAdditional(i, { choice: e.target.value, detailCode: '', detailTitle: '', template: '' }); setStatus(null); }}>
                      <option value="" disabled>Choose an industry group</option>
                      {(sectors ?? []).map((s) => <option key={s.code} value={s.code}>{s.title}</option>)}
                      <option value={CUSTOM}>Other or custom industry</option>
                    </select>
                  </div>
                  {row.choice !== '' && (
                    <div className="jr-field">
                      <label htmlFor={`${uid}-extra-${i}-text`}>{row.choice === CUSTOM ? 'Describe this industry' : 'In your own words (optional)'}</label>
                      <input id={`${uid}-extra-${i}-text`} value={row.description} maxLength={INDUSTRY_DESCRIPTION_MAX} autoComplete="off" disabled={busy} onChange={(e) => { updateAdditional(i, { description: e.target.value }); setStatus(null); }} />
                    </div>
                  )}
                  <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={busy} aria-label={`Remove additional industry ${i + 1}`} onClick={() => { setAdditional((rows) => rows.filter((_, j) => j !== i)); setStatus(null); }}>Remove</button>
                </div>
              ))}
            </fieldset>
          )}

          <div className="jr-actions">
            {additional.length < MAX_ADDITIONAL_INDUSTRIES && (
              <button type="button" className="jr-btn jr-btn--secondary" disabled={busy} onClick={() => { setAdditional((rows) => [...rows, { choice: '', detailCode: '', detailTitle: '', description: '', template: '' }]); setStatus(null); }}>
                <span aria-hidden="true">+ </span>Add another industry
              </button>
            )}
            <button type="button" className="jr-btn jr-btn--primary" disabled={!changed || busy || problem !== null} onClick={() => void save()}>{busy ? 'Saving…' : 'Save industry'}</button>
          </div>
          {changed && problem && <p className="jr-hint" role="note">{problem}</p>}
          {changed && !problem && !busy && catalogs && (
            <IndustryChangePreviewPanel input={{ primary: toInput(draft), additional: additional.map(toInput) }} serviceTypes={catalogs.serviceTypes} disciplines={catalogs.disciplines} />
          )}
        </>
      )}

      {profile.updatedAtUtc && <p className="jr-hint">Last changed {profile.updatedAtUtc.slice(0, 10)}{profile.naicsVersion ? ` · industry list: NAICS ${profile.naicsVersion}` : ''}.</p>}
      {status && <p className="jr-ok" role="status">{status}</p>}
      {error && (
        <p className="jr-error jr-error--block" role="alert">
          {error}{' '}
          {conflict && <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={onReload}>Reload</button>}
        </p>
      )}
    </section>
  );
}

export type { IndustryKind };
